import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { CSVLink } from 'react-csv';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../Auth/Auth';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { CalendarDaysIcon, DocumentTextIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Monthly Job Report', to: '/analytics/monthly-jobs', current: true }
];

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthlyJobReport = () => {
  const { role, loading: authLoading } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (role === 'Service Advisor' || role === 'Admin') {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 1);

      const jobsRef = collection(db, 'jobs');
      const qy = query(
        jobsRef,
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<', Timestamp.fromDate(end))
      );
      const snap = await getDocs(qy);

      const jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Fetch vehicles and customers in bulk for better performance
      const vehiclesSnapshot = await getDocs(collection(db, 'vehicles'));
      const customersSnapshot = await getDocs(collection(db, 'customers'));

      // Create lookup maps
      const vehiclesByReg = {};
      const vehiclesById = {};
      vehiclesSnapshot.forEach(doc => {
        const data = doc.data();
        vehiclesById[doc.id] = data;
        if (data.registrationNumber) {
          vehiclesByReg[data.registrationNumber] = data;
        }
      });

      const customersById = {};
      customersSnapshot.forEach(doc => {
        customersById[doc.id] = doc.data();
      });

      const out = [];
      for (const j of jobs) {
        let vehicleData = null;
        let customerData = null;

        // Try to get vehicle by ID first
        if (j.vehicleId && vehiclesById[j.vehicleId]) {
          vehicleData = vehiclesById[j.vehicleId];
        }
        // Fallback to registration number
        else if (j.registrationNumber && vehiclesByReg[j.registrationNumber]) {
          vehicleData = vehiclesByReg[j.registrationNumber];
        }

        // Get customer data
        if (vehicleData?.customerId && customersById[vehicleData.customerId]) {
          customerData = customersById[vehicleData.customerId];
        }

        const make = vehicleData?.vehicleMake || j.vehicleMake || '';
        const model = vehicleData?.vehicleModel || j.vehicleModel || '';
        const vehicleType = `${make} ${model}`.trim();

        const createdDate = j.createdAt?.toDate ? j.createdAt.toDate() : (j.createdAt ? new Date(j.createdAt) : null);
        const closedDate = j.deliveryDate?.toDate ? j.deliveryDate.toDate() : (j.deliveryDate ? new Date(j.deliveryDate) : null);

        out.push({
          jobCardNo: j.jobCardNo || '',
          customerName: customerData ? `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim() : (j.customerName || ''),
          registrationNumber: vehicleData?.registrationNumber || j.registrationNumber || '',
          vehicleType,
          workDone: j.technicianObservation || '',
          dateCreated: createdDate ? createdDate.toLocaleDateString('en-GB') : '',
          dateClosed: closedDate ? closedDate.toLocaleDateString('en-GB') : '',
          delivered: j.status === 'Closed' ? 'Delivered' : 'Open',
        });
      }

      setRows(out);
    } catch (e) {
      logger.error('Failed to load monthly jobs', e);
      toast.error('Failed to load monthly job report');
    } finally {
      setLoading(false);
    }
  };

  const csvHeaders = [
    { label: 'Job Card No', key: 'jobCardNo' },
    { label: 'Customer Name', key: 'customerName' },
    { label: 'Registration Number', key: 'registrationNumber' },
    { label: 'Vehicle Type', key: 'vehicleType' },
    { label: 'Work Done', key: 'workDone' },
    { label: 'Date Created', key: 'dateCreated' },
    { label: 'Date Closed', key: 'dateClosed' },
    { label: 'Delivered', key: 'delivered' },
  ];

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => [r.jobCardNo, r.customerName, r.registrationNumber, r.vehicleType, r.workDone, r.dateCreated, r.dateClosed]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filteredRows.slice(pageStart, pageStart + pageSize);

  const statusCounts = useMemo(() => {
    let open = 0;
    let delivered = 0;
    filteredRows.forEach(r => {
      if (r.delivered === 'Delivered') delivered += 1; else open += 1;
    });
    return { open, delivered };
  }, [filteredRows]);

  // Chart data for visualization
  const chartData = {
    labels: ['Open', 'Delivered'],
    datasets: [{
      label: 'Job Status',
      data: [statusCounts.open, statusCounts.delivered],
      backgroundColor: [
        'rgba(251, 191, 36, 0.8)',  // Yellow for Open
        'rgba(16, 185, 129, 0.8)'   // Green for Delivered
      ],
      borderColor: [
        'rgb(251, 191, 36)',
        'rgb(16, 185, 129)'
      ],
      borderWidth: 2
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((context.parsed.y / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${context.parsed.y} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
      x: {
        grid: { display: false }
      }
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
        <div className="text-center">
          <MoonLoader size={60} color="#0EA5E9" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-blue-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <CalendarDaysIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Monthly Job Report</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Comprehensive job tracking for {monthNames[month]} {year}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Month:</label>
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium"
                value={month}
                onChange={(e) => {
                  setMonth(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
              >
                {monthNames.map((m, idx) => (
                  <option key={m} value={idx}>{m}</option>
                ))}
              </select>
            </div>

            {/* Year Input */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <input
                type="number"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm font-medium"
                value={year}
                onChange={(e) => {
                  setYear(parseInt(e.target.value || `${now.getFullYear()}`, 10));
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Search Input */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                placeholder="Search jobs, customers, vehicles..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Export CSV Button */}
            <CSVLink
              data={filteredRows}
              headers={csvHeaders}
              filename={`monthly_job_report_${year}_${monthNames[month]}.csv`}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 text-sm font-medium shadow-md transition-all duration-200 flex items-center gap-2"
            >
              <DocumentTextIcon className="w-5 h-5" />
              Export CSV
            </CSVLink>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#0EA5E9" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading job data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Jobs */}
              <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sky-100 text-sm font-medium">Total Jobs</p>
                    <h3 className="text-3xl font-bold mt-1">{filteredRows.length}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <DocumentTextIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-sky-100 text-xs">in {monthNames[month]} {year}</p>
              </div>

              {/* Open Jobs */}
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">Open Jobs</p>
                    <h3 className="text-3xl font-bold mt-1">{statusCounts.open}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ClockIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-amber-100 text-xs">
                  {statusCounts.open + statusCounts.delivered > 0
                    ? `${((statusCounts.open / (statusCounts.open + statusCounts.delivered)) * 100).toFixed(1)}% of total`
                    : '0% of total'}
                </p>
              </div>

              {/* Delivered Jobs */}
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Delivered Jobs</p>
                    <h3 className="text-3xl font-bold mt-1">{statusCounts.delivered}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <CheckCircleIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-emerald-100 text-xs">
                  {statusCounts.open + statusCounts.delivered > 0
                    ? `${((statusCounts.delivered / (statusCounts.open + statusCounts.delivered)) * 100).toFixed(1)}% completion rate`
                    : '0% completion rate'}
                </p>
              </div>
            </div>

            {/* Status Chart */}
            {filteredRows.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Job Status Distribution</h3>
                  <p className="text-sm text-gray-600 mt-1">Visual breakdown of open vs delivered jobs</p>
                </div>
                <div style={{ height: '300px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            )}

            {/* Jobs Table */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-sky-600 to-blue-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">Job Details</h3>
                <p className="text-sky-100 text-sm mt-1">
                  Showing {filteredRows.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pageSize, filteredRows.length)} of {filteredRows.length} jobs
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Card No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Done</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <CalendarDaysIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm">No jobs found for {monthNames[month]} {year}</p>
                            {search && <p className="text-xs mt-1">Try adjusting your search criteria</p>}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((r, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{r.jobCardNo}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{r.customerName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.registrationNumber}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{r.vehicleType}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div className="max-w-xs truncate" title={r.workDone}>
                              {r.workDone || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{r.dateCreated}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{r.dateClosed || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              r.delivered === 'Delivered'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {r.delivered}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredRows.length > 0 && (
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlyJobReport;
